import React, { useState } from 'react';
import { Plus, Search, Edit, Trash2, Settings, Bell, Target, MapPin, Building, FileText, AlertCircle, Save, X, Package, RefreshCw, Zap, DollarSign } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { supabase } from '../lib/supabase';
import { BusinessLine } from '../types';
import BolsaLicitaciones from './BolsaLicitaciones';
import { useTenant } from '../contexts/TenantContext';

const AccountSettings: React.FC = () => {
  const { businessLines, organizations, addBusinessLine, updateBusinessLine, deleteBusinessLine } = useData();
  const { currentCompany } = useTenant();
  const [showForm, setShowForm] = useState(false);
  const [editingLine, setEditingLine] = useState<BusinessLine | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'business-lines' | 'notifications' | 'preferences' | 'bolsa-licitaciones'>('business-lines');
  const [isSearching, setIsSearching] = useState<Record<string, boolean>>({});
  const [searchResults, setSearchResults] = useState<Record<string, { matched: number; skipped: number }>>({});

  // Check if current user is super admin (Licitatrak)
  const isSuperAdmin = currentCompany?.rut === '99.999.999-9';
  
  // Verificar si el onboarding ya fue completado
  const onboardingCompleted = localStorage.getItem('licitatrak-onboarding-completed');

  const [formData, setFormData] = useState({
    name: '',
    keywords: [] as string[],
    regions: [] as string[],
    sectors: [] as string[],
    organizations: [] as string[],
    tender_types: ['licitaciones'] as ('licitaciones' | 'compra_agil' | 'trato_directo')[],
    is_active: true
  });

  const [newKeyword, setNewKeyword] = useState('');
  const [newRegion, setNewRegion] = useState('');
  const [newSector, setNewSector] = useState('');
  const [newOrganization, setNewOrganization] = useState('');

  // Regiones de Chile
  const chileanRegions = [
    'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
    'Valparaíso', 'Metropolitana', 'O\'Higgins', 'Maule', 'Ñuble',
    'Biobío', 'La Araucanía', 'Los Ríos', 'Los Lagos', 'Aysén',
    'Magallanes y Antártica Chilena'
  ];

  // Rubros/Sectores comunes
  const commonSectors = [
    'Construcción', 'Tecnología', 'Salud', 'Educación', 'Transporte',
    'Energía', 'Medio Ambiente', 'Seguridad', 'Alimentación', 'Textil',
    'Minería', 'Agricultura', 'Turismo', 'Telecomunicaciones', 'Financiero'
  ];

  const filteredBusinessLines = businessLines.filter(line =>
    line.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    line.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar que haya al menos un nombre
    if (!formData.name.trim()) {
      alert('El nombre de la línea de negocio es obligatorio.');
      return;
    }

    // Obtener límites desde la base de datos
    const maxWords = currentCompany?.business_word || 10;
    
    if (formData.keywords.length > maxWords) {
      alert(`No se pueden agregar más de ${maxWords} palabras clave por línea de negocio.`);
      return;
    }
    
    const lineData = {
      ...formData,
      keywords: formData.keywords.filter(k => k.trim() !== ''),
      regions: formData.regions.filter(r => r.trim() !== ''),
      sectors: formData.sectors.filter(s => s.trim() !== ''),
      organizations: formData.organizations
    };

    try {
      if (editingLine) {
        await updateBusinessLine(editingLine.id, lineData);
      } else {
        const success = await addBusinessLine(lineData);
        if (success) {
          console.log('Business line created successfully');
          resetForm();
        }
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      // Don't reset form if there was an error
    }
  };

  const handleEdit = (line: BusinessLine) => {
    setEditingLine(line);
    setFormData({
      name: line.name,
      keywords: [...line.keywords],
      regions: [...line.regions],
      sectors: [...line.sectors],
      organizations: line.organizations || [],
      tender_types: [...line.tender_types],
      is_active: line.is_active
    });
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta línea de negocio?')) {
      deleteBusinessLine(id);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      keywords: [],
      regions: [],
      sectors: [],
      organizations: [],
      tender_types: ['licitaciones'],
      is_active: true
    });
    setNewKeyword('');
    setNewRegion('');
    setNewSector('');
    setNewOrganization('');
    setEditingLine(null);
    setShowForm(false);
  };

  // Function to execute business line search
  const handleBusinessLineSearch = async (businessLine: BusinessLine) => {
    if (!currentCompany) return;

    setIsSearching(prev => ({ ...prev, [businessLine.id]: true }));
    // Clear previous results
    setSearchResults(prev => {
      const newResults = { ...prev };
      delete newResults[businessLine.id];
      return newResults;
    });

    try {
      // Call the RPC function to process this specific business line
      const { data, error } = await supabase.rpc('process_single_business_line', { 
        p_business_line_id: businessLine.id
      });

      if (error) {
        console.error('Error from RPC function:', error);
        throw new Error(`Error al procesar la línea de negocio: ${error.message || 'Error desconocido'}`);
      }

      // Count the results
      const results = data || [];
      console.log('Search results:', results);

      // Check if there's an error message in the results
      const errorResult = results.find((r: any) => r.message && r.message.startsWith('Function error:'));
      if (errorResult) {
        throw new Error(errorResult.message);
      }

      // Count matched and skipped tenders
      let matchedCount = 0;
      let skippedCount = 0;
      
      for (const result of results) {
        if (result.matched === true) {
          matchedCount++;
        } else if (result.matched === false) {
          skippedCount++;
        }
      }

      // Update the results
      setSearchResults(prev => ({ 
        ...prev, 
        [businessLine.id]: { 
          matched: matchedCount, 
          skipped: skippedCount 
        } 
      }));
      
      // Show the results for 5 seconds, then clear
      const hasResults = matchedCount > 0 || skippedCount > 0;
      if (hasResults) {
        setTimeout(() => {
          setSearchResults(prev => {
            const newResults = { ...prev };
            delete newResults[businessLine.id];
            return newResults;
          });
        }, 5000);
      }
    } catch (error) {
      console.error('Error searching business line:', error);
      alert(`Error al buscar licitaciones: ${error instanceof Error ? error.message : 'Error desconocido'}`);
      
      // Clear the searching state
      setIsSearching(prev => ({ ...prev, [businessLine.id]: false }));
      return;
    } finally {
      setIsSearching(prev => ({ ...prev, [businessLine.id]: false }));
    }
  };

  const addKeyword = () => {
    const maxWords = currentCompany?.business_word || 10;
    if (newKeyword.trim() && !formData.keywords.includes(newKeyword.trim()) && formData.keywords.length < maxWords) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, newKeyword.trim()]
      }));
      setNewKeyword('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const addRegion = () => {
    if (newRegion && !formData.regions.includes(newRegion)) {
      setFormData(prev => ({
        ...prev,
        regions: [...prev.regions, newRegion]
      }));
      setNewRegion('');
    }
  };

  const removeRegion = (region: string) => {
    setFormData(prev => ({
      ...prev,
      regions: prev.regions.filter(r => r !== region)
    }));
  };

  const addSector = () => {
    if (newSector && !formData.sectors.includes(newSector)) {
      setFormData(prev => ({
        ...prev,
        sectors: [...prev.sectors, newSector]
      }));
      setNewSector('');
    }
  };

  const removeSector = (sector: string) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.filter(s => s !== sector)
    }));
  };

  const addOrganization = () => {
    if (newOrganization && !formData.organizations.includes(newOrganization)) {
      setFormData(prev => ({
        ...prev,
        organizations: [...prev.organizations, newOrganization]
      }));
      setNewOrganization('');
    }
  };

  const removeOrganization = (orgId: string) => {
    setFormData(prev => ({
      ...prev,
      organizations: prev.organizations.filter(id => id !== orgId)
    }));
  };

  const toggleTenderType = (type: 'licitaciones' | 'compra_agil' | 'trato_directo') => {
    setFormData(prev => ({
      ...prev,
      tender_types: prev.tender_types.includes(type)
        ? prev.tender_types.filter(t => t !== type)
        : [...prev.tender_types, type]
    }));
  };

  const getOrganizationName = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : 'Organización no encontrada';
  };

  const tabs = [
    { id: 'business-lines', label: 'Líneas de Negocio', icon: Target },
    ...(isSuperAdmin ? [{ id: 'bolsa-licitaciones', label: 'Bolsa de Licitaciones', icon: Package }] : []),
    { id: 'notifications', label: 'Notificaciones', icon: Bell },
    { id: 'preferences', label: 'Preferencias', icon: Settings }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Perfil</h1>
          <p className="text-gray-600">Configura tus líneas de negocio y preferencias de búsqueda</p>
        </div>
        {activeTab === 'business-lines' && (
          <div className="flex flex-col items-end">
            {(() => {
              const isLite = currentCompany?.islite === true;
              const maxBusinessLines = currentCompany?.business_line || 10;
              const isDisabled = businessLines.length >= maxBusinessLines || !currentCompany?.accept;
              
              return (
                <>
                  <button
                    onClick={() => {
                      if (!currentCompany?.accept) {
                        alert('Debe aceptar los términos y condiciones antes de crear líneas de negocio. Vaya a "Políticas de Privacidad" en el menú para aceptar los términos.');
                        return;
                      }
                      setShowForm(true);
                    }}
                    disabled={isDisabled}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
                      isDisabled
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Nueva Línea de Negocio
                  </button>
                  {isDisabled && (
                    <p className="text-xs text-gray-500 mt-1 text-right max-w-xs">
                      {!currentCompany?.accept 
                        ? 'Debe aceptar los términos y condiciones primero'
                        : isLite 
                          ? `La versión lite permite solo ${maxBusinessLines} línea de negocio`
                          : `Has alcanzado el límite de ${maxBusinessLines} líneas de negocio`
                      }
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'business-lines' && (
            <div className="space-y-6">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar líneas de negocio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Business Lines Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredBusinessLines.length > 0 ? (
                  filteredBusinessLines.map((line) => (
                    <div key={line.id} className="bg-gray-50 rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {line.name}
                          </h3>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            line.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {line.is_active ? 'Activa' : 'Inactiva'}
                          </span>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleEdit(line)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                            title="Editar línea de negocio"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleBusinessLineSearch(line)}
                            disabled={isSearching[line.id]}
                            className={`p-2 rounded-lg transition-colors ${
                              isSearching[line.id]
                                ? 'text-blue-400 bg-blue-50 cursor-wait'
                                : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                            }`}
                            title="Buscar licitaciones para esta línea de negocio"
                          >
                            {isSearching[line.id] ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Zap className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(line.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" 
                            title="Eliminar línea de negocio"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {/* Keywords */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium text-gray-700">Palabras Clave:</p>
                            {searchResults[line.id] && (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                searchResults[line.id].matched > 0 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {searchResults[line.id].matched > 0 
                                  ? `${searchResults[line.id].matched} nuevas` 
                                  : ''
                                }
                                {searchResults[line.id].matched > 0 && searchResults[line.id].skipped > 0 ? ', ' : ''}
                                {searchResults[line.id].skipped > 0 
                                  ? `${searchResults[line.id].skipped} existentes` 
                                  : searchResults[line.id].matched === 0 ? 'No hay coincidencias' : ''
                                }
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {line.keywords.slice(0, 3).map((keyword, index) => (
                              <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                {keyword}
                              </span>
                            ))}
                            {line.keywords.length > 3 && (
                              <span className="text-xs text-gray-500">+{line.keywords.length - 3} más</span>
                            )}
                          </div>
                        </div>

                        {/* Organizations */}
                        {line.organizations && line.organizations.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Organizaciones:</p>
                            <div className="flex flex-wrap gap-1">
                              {line.organizations.slice(0, 2).map((orgId, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800">
                                  {getOrganizationName(orgId)}
                                </span>
                              ))}
                              {line.organizations.length > 2 && (
                                <span className="text-xs text-gray-500">+{line.organizations.length - 2} más</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Regions */}
                        {line.regions.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Regiones:</p>
                            <div className="flex flex-wrap gap-1">
                              {line.regions.slice(0, 2).map((region, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                  {region}
                                </span>
                              ))}
                              {line.regions.length > 2 && (
                                <span className="text-xs text-gray-500">+{line.regions.length - 2} más</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Sectors */}
                        {line.sectors.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Rubros:</p>
                            <div className="flex flex-wrap gap-1">
                              {line.sectors.slice(0, 2).map((sector, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                                  {sector}
                                </span>
                              ))}
                              {line.sectors.length > 2 && (
                                <span className="text-xs text-gray-500">+{line.sectors.length - 2} más</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Tender Types */}
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Tipos de Licitación:</p>
                          <div className="flex flex-wrap gap-1">
                            {line.tender_types.map((type, index) => (
                              <span key={index} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                type === 'licitaciones' ? 'bg-blue-100 text-blue-800' :
                                type === 'compra_agil' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {type === 'licitaciones' ? 'Licitaciones' :
                                 type === 'compra_agil' ? 'Compra Ágil' :
                                 'Trato Directo'}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full">
                    {searchTerm ? (
                      // Estado de búsqueda sin resultados
                      <div className="text-center py-12">
                        <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay líneas de negocio</h3>
                        <p className="text-gray-600 mb-6">
                          No se encontraron líneas de negocio con los filtros aplicados.
                        </p>
                        <button
                          onClick={() => setSearchTerm('')}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                        >
                          Limpiar filtros
                        </button>
                      </div>
                    ) : onboardingCompleted ? (
                      // Página de bienvenida instructiva
                      <div className="max-w-4xl mx-auto">
                        {/* Header de bienvenida */}
                        <div className="text-center py-8 mb-8">
                          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-4">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <h2 className="text-3xl font-bold text-gray-900 mb-2">¡Bienvenido a Licitatrak!</h2>
                          <p className="text-lg text-gray-600 mb-6">
                            Te ayudaremos a configurar tu cuenta paso a paso para encontrar las mejores oportunidades de licitación
                          </p>
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <h4 className="text-sm font-medium text-blue-900 mb-1">
                                  Versión Gratuita
                                </h4>
                                <p className="text-sm text-blue-700">
                                  Puedes crear <strong>1 línea de negocio</strong> con máximo <strong>3 palabras clave</strong>. 
                                  Para más líneas y funcionalidades avanzadas,{' '}
                                  <a 
                                    href="https://www.licitatrak.cl" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="font-medium text-blue-800 hover:text-blue-900 underline"
                                  >
                                    puedes revisar nuestros planes en nuestro portal web
                                  </a>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Pasos instructivos */}
                        <div className="space-y-6">
                          {/* Paso 1 */}
                          <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                                  <span className="text-lg font-bold text-blue-600">1</span>
                                </div>
                              </div>
                              <div className="ml-4 flex-1">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Primer Paso: Crear Líneas de Negocio</h3>
                                    <p className="text-gray-600 mb-4">
                                      Define las áreas de tu empresa para las que quieres recibir alertas de licitaciones. 
                                      Puedes agregar palabras clave específicas que se buscarán en los títulos de las licitaciones.
                                    </p>
                                    <div className="flex items-center text-sm text-gray-500 mb-4">
                                      <Target className="h-4 w-4 mr-2" />
                                      <span>Configura qué tipo de licitaciones te interesan</span>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => setShowForm(true)}
                                    className="ml-4 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Crear Línea
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Paso 2 */}
                          <div className="bg-white rounded-lg border border-gray-200 p-6 opacity-75">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full">
                                  <span className="text-lg font-bold text-gray-400">2</span>
                                </div>
                              </div>
                              <div className="ml-4 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Segundo Paso: Crear Productos</h3>
                                <p className="text-gray-600 mb-4">
                                  Registra los productos y servicios que tu empresa ofrece. Esto te ayudará a identificar 
                                  rápidamente las licitaciones más relevantes para tu negocio.
                                </p>
                                <div className="flex items-center text-sm text-gray-500 mb-4">
                                  <Package className="h-4 w-4 mr-2" />
                                  <span>Catálogo de productos y servicios</span>
                                </div>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                  <p className="text-sm text-yellow-800">
                                    <strong>Próximo:</strong> Este paso estará disponible después de crear tu línea de negocio.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Paso 3 */}
                          <div className="bg-white rounded-lg border border-gray-200 p-6 opacity-75">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full">
                                  <span className="text-lg font-bold text-gray-400">3</span>
                                </div>
                              </div>
                              <div className="ml-4 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Tercer Paso: Completar Tasas de Cambio</h3>
                                <p className="text-gray-600 mb-4">
                                  Configura las tasas de cambio de monedas para calcular correctamente los montos de las licitaciones 
                                  y tomar mejores decisiones de negocio.
                                </p>
                                <div className="flex items-center text-sm text-gray-500 mb-4">
                                  <DollarSign className="h-4 w-4 mr-2" />
                                  <span>Conversión automática de monedas</span>
                                </div>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                  <p className="text-sm text-yellow-800">
                                    <strong>Próximo:</strong> Este paso estará disponible después de completar los pasos anteriores.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Footer de agradecimiento */}
                        <div className="text-center py-8 mt-8">
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 max-w-md mx-auto">
                            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto mb-3">
                              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                              </svg>
                            </div>
                            <h4 className="text-lg font-semibold text-green-900 mb-2">¡Gracias por preferirnos!</h4>
                            <p className="text-green-700 text-sm">
                              Estamos aquí para ayudarte a encontrar las mejores oportunidades de licitación. 
                              Si tienes alguna pregunta, no dudes en contactarnos.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Mensaje simple cuando el onboarding no ha sido completado
                      <div className="text-center py-12">
                        <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay líneas de negocio</h3>
                        <p className="text-gray-600 mb-6">
                          Comienza agregando tu primera línea de negocio para recibir alertas personalizadas.
                        </p>
                        <button
                          onClick={() => setShowForm(true)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                        >
                          Nueva Línea de Negocio
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'bolsa-licitaciones' && (
            isSuperAdmin ? <BolsaLicitaciones /> : (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Restringido</h3>
                <p className="text-gray-600">
                  La Bolsa de Licitaciones solo está disponible para administradores de Licitatrak.
                </p>
              </div>
            )
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              {/* Configuración de notificaciones deshabilitada */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Configuración de Notificaciones</h3>
                    <p className="text-sm text-gray-600">Personaliza cómo recibes alertas sobre nuevas licitaciones</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg px-3 py-1">
                    <span className="text-xs font-medium text-blue-800">Funcionalidad Premium</span>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Email Notifications */}
                  <div className="border border-gray-200 rounded-lg p-4 opacity-60">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg mr-3">
                          <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Notificaciones por Email</h4>
                          <p className="text-sm text-gray-600">Recibe alertas por correo electrónico</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          disabled
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-500">Deshabilitado</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input type="checkbox" disabled className="h-3 w-3 text-blue-600 border-gray-300 rounded mr-2" />
                        <span className="text-sm text-gray-500">Nuevas licitaciones que coincidan con mis líneas de negocio</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" disabled className="h-3 w-3 text-blue-600 border-gray-300 rounded mr-2" />
                        <span className="text-sm text-gray-500">Licitaciones próximas a cerrar (3 días antes)</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" disabled className="h-3 w-3 text-blue-600 border-gray-300 rounded mr-2" />
                        <span className="text-sm text-gray-500">Cambios de estado en mis postulaciones</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" disabled className="h-3 w-3 text-blue-600 border-gray-300 rounded mr-2" />
                        <span className="text-sm text-gray-500">Adjudicaciones (ganadas o perdidas)</span>
                      </label>
                    </div>
                  </div>

                  {/* SMS Notifications */}
                  <div className="border border-gray-200 rounded-lg p-4 opacity-60">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg mr-3">
                          <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Notificaciones SMS</h4>
                          <p className="text-sm text-gray-600">Alertas urgentes por mensaje de texto</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          disabled
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-500">Deshabilitado</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input type="checkbox" disabled className="h-3 w-3 text-green-600 border-gray-300 rounded mr-2" />
                        <span className="text-sm text-gray-500">Oportunidades de alto valor (&gt;$10M)</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" disabled className="h-3 w-3 text-green-600 border-gray-300 rounded mr-2" />
                        <span className="text-sm text-gray-500">Licitaciones con vencimiento en 24 horas</span>
                      </label>
                    </div>
                  </div>

                  {/* Push Notifications */}
                  <div className="border border-gray-200 rounded-lg p-4 opacity-60">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg mr-3">
                          <Bell className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Notificaciones Push</h4>
                          <p className="text-sm text-gray-600">Alertas en tiempo real en tu dispositivo</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          disabled
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-500">Deshabilitado</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input type="checkbox" disabled className="h-3 w-3 text-purple-600 border-gray-300 rounded mr-2" />
                        <span className="text-sm text-gray-500">Notificaciones instantáneas</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" disabled className="h-3 w-3 text-purple-600 border-gray-300 rounded mr-2" />
                        <span className="text-sm text-gray-500">Recordatorios de calendario</span>
                      </label>
                    </div>
                  </div>

                  {/* Schedule Settings */}
                  <div className="border border-gray-200 rounded-lg p-4 opacity-60">
                    <div className="flex items-center mb-3">
                      <div className="p-2 bg-orange-100 rounded-lg mr-3">
                        <svg className="h-5 w-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Horarios de Notificación</h4>
                        <p className="text-sm text-gray-600">Configura cuándo recibir alertas</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Horario de inicio</label>
                        <input
                          type="time"
                          disabled
                          value="09:00"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Horario de fin</label>
                        <input
                          type="time"
                          disabled
                          value="18:00"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="flex items-center">
                        <input type="checkbox" disabled className="h-3 w-3 text-orange-600 border-gray-300 rounded mr-2" />
                        <span className="text-sm text-gray-500">Solo días hábiles (Lunes a Viernes)</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mensaje de upgrade */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-blue-900 mb-2">
                      Desbloquea Notificaciones Avanzadas
                    </h3>
                    <p className="text-blue-700 mb-4">
                      Con un plan pagado podrás activar todas estas funcionalidades y personalizar completamente 
                      cómo recibes alertas sobre nuevas oportunidades de licitación.
                    </p>
                    <div className="flex items-center space-x-4">
                      <a 
                        href="https://www.licitatrak.cl" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Puedes revisar nuestros planes en nuestro portal web
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-6">
              {/* Mensaje de funcionalidad premium */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-blue-900 mb-2">
                      Preferencias - Funcionalidad Premium
                    </h3>
                    <p className="text-blue-700 mb-4">
                      La configuración avanzada de preferencias está disponible en nuestros planes pagados. 
                      Con esta funcionalidad podrás:
                    </p>
                    <ul className="text-blue-700 space-y-2 mb-4">
                      <li className="flex items-center">
                        <svg className="h-4 w-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Configurar búsquedas automáticas personalizadas
                      </li>
                      <li className="flex items-center">
                        <svg className="h-4 w-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Establecer rangos de montos específicos
                      </li>
                      <li className="flex items-center">
                        <svg className="h-4 w-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Configurar frecuencia de alertas personalizada
                      </li>
                      <li className="flex items-center">
                        <svg className="h-4 w-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Personalizar dashboard y vistas
                      </li>
                    </ul>
                    <div className="flex items-center space-x-4">
                      <a 
                        href="https://www.licitatrak.cl" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Puedes revisar nuestros planes en nuestro portal web
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingLine ? 'Editar Línea de Negocio' : 'Nueva Línea de Negocio'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de la Línea de Negocio *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ej: Construcción de Infraestructura"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
                    Línea de negocio activa
                  </label>
                </div>
              </div>

              {/* Keywords */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Palabras Clave para Búsqueda
                  </label>
                  <span className="text-xs text-gray-500">
                    {formData.keywords.length}/{currentCompany?.business_word || 10} palabras
                  </span>
                </div>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={formData.keywords.length >= (currentCompany?.business_word || 10) ? `Máximo ${currentCompany?.business_word || 10} palabras alcanzado` : "Agregar palabra clave"}
                    disabled={formData.keywords.length >= (currentCompany?.business_word || 10)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                  />
                  <button
                    type="button"
                    onClick={addKeyword}
                    disabled={formData.keywords.length >= (currentCompany?.business_word || 10) || !newKeyword.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Agregar
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.keywords.map((keyword, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                      {keyword}
                      <button
                        type="button"
                        onClick={() => removeKeyword(keyword)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                
                {/* Aviso cuando se alcanza el límite */}
                {formData.keywords.length >= (currentCompany?.business_word || 10) && (
                  <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-blue-900">
                          ¡Has alcanzado el límite de palabras clave!
                        </h4>
                        <p className="text-sm text-blue-700 mt-1">
                          {currentCompany?.islite 
                            ? `La versión gratuita permite máximo ${currentCompany?.business_word || 3} palabras clave por línea de negocio. Para agregar más palabras clave, crear múltiples líneas de negocio y acceder a funcionalidades avanzadas, puedes revisar nuestros planes en nuestro portal web.`
                            : `Has alcanzado el límite de ${currentCompany?.business_word || 10} palabras clave por línea de negocio.`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-1">
                  Estas palabras se buscarán en los títulos de las licitaciones. Máximo {currentCompany?.business_word || 10} palabras por línea de negocio.
                </p>
              </div>

              {/* Organizations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organizaciones de Interés
                </label>
                <div className="flex gap-2 mb-3">
                  <select
                    value={newOrganization}
                    onChange={(e) => setNewOrganization(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar organización</option>
                    {organizations
                      .filter(org => !formData.organizations.includes(org.id))
                      .map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))
                    }
                  </select>
                  <button
                    type="button"
                    onClick={addOrganization}
                    disabled={!newOrganization}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    Agregar
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.organizations.map((orgId, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800">
                      <Building className="h-3 w-3 mr-1" />
                      {getOrganizationName(orgId)}
                      <button
                        type="button"
                        onClick={() => removeOrganization(orgId)}
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Filtra licitaciones por organizaciones específicas de tu interés
                </p>
              </div>

              {/* Regions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regiones de Interés
                </label>
                <div className="flex gap-2 mb-3">
                  <select
                    value={newRegion}
                    onChange={(e) => setNewRegion(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar región</option>
                    {chileanRegions.filter(region => !formData.regions.includes(region)).map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={addRegion}
                    disabled={!newRegion}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    Agregar
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.regions.map((region, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                      <MapPin className="h-3 w-3 mr-1" />
                      {region}
                      <button
                        type="button"
                        onClick={() => removeRegion(region)}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Sectors */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rubros/Sectores
                </label>
                <div className="flex gap-2 mb-3">
                  <select
                    value={newSector}
                    onChange={(e) => setNewSector(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar rubro</option>
                    {commonSectors.filter(sector => !formData.sectors.includes(sector)).map(sector => (
                      <option key={sector} value={sector}>{sector}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={addSector}
                    disabled={!newSector}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    Agregar
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.sectors.map((sector, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                      <Building className="h-3 w-3 mr-1" />
                      {sector}
                      <button
                        type="button"
                        onClick={() => removeSector(sector)}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Tender Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Tipos de Licitación
                </label>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="licitaciones"
                      checked={formData.tender_types.includes('licitaciones')}
                      onChange={() => toggleTenderType('licitaciones')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="licitaciones" className="ml-3 flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Licitaciones</span>
                      <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">Disponible</span>
                    </label>
                  </div>
                  
                  <div className="flex items-center opacity-50">
                    <input
                      type="checkbox"
                      id="compra_agil"
                      checked={formData.tender_types.includes('compra_agil')}
                      onChange={() => toggleTenderType('compra_agil')}
                      disabled
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="compra_agil" className="ml-3 flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-orange-600" />
                      <span className="text-sm font-medium text-gray-700">Compra Ágil</span>
                      <span className="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">Próximamente</span>
                    </label>
                  </div>
                  
                  <div className="flex items-center opacity-50">
                    <input
                      type="checkbox"
                      id="trato_directo"
                      checked={formData.tender_types.includes('trato_directo')}
                      onChange={() => toggleTenderType('trato_directo')}
                      disabled
                      className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                    />
                    <label htmlFor="trato_directo" className="ml-3 flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Trato Directo</span>
                      <span className="ml-2 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">Próximamente</span>
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Compra Ágil y Trato Directo estarán disponibles en futuras versiones
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">¿Cómo funcionan las líneas de negocio?</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Las palabras clave se buscan en los títulos de las licitaciones</li>
                      <li>• Los filtros de organización, región y rubro ayudan a segmentar las oportunidades</li>
                      {currentCompany?.islite ? (
                        <>
                          <li>• <strong>Versión Lite:</strong> {currentCompany?.business_line || 1} línea de negocio con máximo {currentCompany?.business_word || 3} palabras clave</li>
                          <li>• <strong>Notificaciones:</strong> Limitadas según el plan</li>
                        </>
                      ) : (
                        <>
                          <li>• <strong>Versión Completa:</strong> Hasta {currentCompany?.business_line || 10} líneas de negocio</li>
                          <li>• <strong>Palabras clave:</strong> Máximo {currentCompany?.business_word || 10} por línea</li>
                          <li>• <strong>Notificaciones:</strong> Completas según el plan</li>
                        </>
                      )}
                      <li>• Las líneas inactivas no aparecerán en las búsquedas automáticas</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingLine ? 'Actualizar' : 'Crear'} Línea de Negocio
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;